import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import {
  doCreate,
  doDelete,
  doUpdate,
  luHandleApiError,
  type U3147CreateManualRequest,
  type U3147DeleteRequest,
  type U3147TimeSession,
  type U3147UpdateRequest,
} from './U3147Service';
import { NS } from './U3147';

function unwrapSession(res: { data?: { result?: unknown } }): U3147TimeSession | null {
  const raw = res.data?.result ?? res.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as U3147TimeSession;
  }
  return null;
}

export function useU3147ManualSessionHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const createSession = useCallback(
    async (body: U3147CreateManualRequest) => {
      setBusy(true);
      try {
        const response = await doCreate(axios, body);
        toast.success(t('msgSessionSaved', { ns: NS, defaultValue: 'Plage enregistrée.' }));
        return unwrapSession(response);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [axios, t, toast],
  );

  const updateSession = useCallback(
    async (id: string, body: U3147UpdateRequest) => {
      setBusy(true);
      try {
        const response = await doUpdate(axios, id, body);
        toast.success(t('msgSessionSaved', { ns: NS, defaultValue: 'Plage enregistrée.' }));
        return unwrapSession(response);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [axios, t, toast],
  );

  const deleteSession = useCallback(
    async (body: U3147DeleteRequest) => {
      setBusy(true);
      try {
        await doDelete(axios, body.id, body);
        toast.success(t('msgSessionDeleted', { ns: NS, defaultValue: 'Plage supprimée.' }));
        return true;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [axios, t, toast],
  );

  return { busy, createSession, updateSession, deleteSession };
}
